import React, { useState, useMemo, useDeferredValue } from 'react'
import DataTable, { TableColumn } from 'react-data-table-component'
import { Card, Form } from 'react-bootstrap'
import { DataTableStyleSheetManager } from '../_table/data-table-style-sheet-manager'
import { ExportRow, TableExportActions, sanitizeExportFilename } from '../_table/table-export-actions'

const dashboardTableBlue = '#5b9bd5'

interface ClickDataTableProps<T> {
    title: string
    columns: TableColumn<T>[]
    data: T[]
    height?:string
    id:string
    paginationPerPage?: number
    paginationRowsPerPageOptions?: number[]
    headerActions?: React.ReactNode
    showTitle?: boolean
    showSearch?: boolean
    showExportActions?: boolean
    exportDataVersion?: unknown
    headerActionsFullWidth?: boolean
    flush?: boolean
    onRowClicked?: (row: T) => void
    pointerOnHover?: boolean
    customStyles?: any
}

const toExportValue = (value: unknown): string | number | boolean | null | undefined => {
    if (value == null) return ''
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value
    if (Array.isArray(value)) return value.join(', ')
    if (value instanceof Date) return value.toISOString()
    return String(value)
}

const toColumnLabel = <T extends { [key: string]: any }>(column: TableColumn<T>, index: number) => {
    if (typeof column.name === 'string') return column.name
    return `Column ${index + 1}`
}

const buildExportRows = <T extends { [key: string]: any }>(columns: TableColumn<T>[], rows: T[]): ExportRow[] => {
    const exportableColumns = columns.filter((column) => !column.omit && !column.button)

    return rows.map((row, rowIndex) => {
        return exportableColumns.reduce<ExportRow>((accumulator, column, columnIndex) => {
            const label = toColumnLabel(column, columnIndex)
            const formattedValue = column.format ? column.format(row, rowIndex) : undefined
            const selectedValue = typeof formattedValue !== 'undefined'
                ? formattedValue
                : typeof column.selector === 'function'
                    ? column.selector(row, rowIndex)
                    : row[label]

            accumulator[label] = toExportValue(selectedValue)
            return accumulator
        }, {})
    })
}

export function ClickDataTable<T extends { [key: string]: any }>({
    title,
    columns,
    data,
    height,
    id,
    paginationPerPage = 50,
    paginationRowsPerPageOptions = [50, 100, 200, 500],
    headerActions,
    showTitle = true,
    showSearch = true,
    showExportActions = true,
    exportDataVersion,
    headerActionsFullWidth = false,
    flush = false,
    onRowClicked,
    pointerOnHover,
    customStyles
}: ClickDataTableProps<T>) {
    const [filterText, setFilterText] = useState('')
    const deferredFilterText = useDeferredValue(filterText)
    const showHeader = showTitle || showSearch || showExportActions || !!headerActions

    const searchIndex = useMemo(() => {
        return data.map((row) => Object.values(row).join(' ').toLowerCase())
    }, [data])

    const filteredData = useMemo(() => {
        const query = deferredFilterText.trim().toLowerCase()
        if (!query) return data

        return data.filter((_, index) => searchIndex[index]?.includes(query))
    }, [data, deferredFilterText, searchIndex])

    const exportRows = useMemo(
        () => buildExportRows(columns, filteredData),
        // Some tables have interactive cell renderers whose identity changes without
        // changing the exportable values. They can provide a stable data version to
        // avoid rebuilding every export row for a cell-only state update.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [exportDataVersion === undefined ? columns : exportDataVersion, filteredData]
    )
    const exportFilename = useMemo(() => sanitizeExportFilename(title), [title])
    const dataTableStyles = useMemo(() => {
        const baseStyles: any = {
            table:{style:{width:'100%', minWidth: '100%'}},
            cells: {
                style: {
                    borderBottom: '1px solid rgba(0,0,0,.12)',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                },

            },
            rows: {
                style: {
                    borderBottom: 'none !important',
                },
            },
            headRow: {
                style: {
                    borderBottom: 'none !important',
                },
            },
            headCells: {
                style: {
                    borderBottom: '1px solid rgba(0,0,0,.12)',
                    backgroundColor: dashboardTableBlue,
                    color: '#ffffff',
                    fontWeight: 700,
                    letterSpacing: '0',
                    textTransform: 'uppercase',
                },
            }
        }

        if (!customStyles) return baseStyles

        return {
            ...baseStyles,
            ...customStyles,
            table: {
                ...baseStyles.table,
                ...customStyles.table,
                style: {
                    ...baseStyles.table?.style,
                    ...customStyles.table?.style,
                },
            },
            cells: {
                ...baseStyles.cells,
                ...customStyles.cells,
                style: {
                    ...baseStyles.cells?.style,
                    ...customStyles.cells?.style,
                },
            },
            rows: {
                ...baseStyles.rows,
                ...customStyles.rows,
                style: {
                    ...baseStyles.rows?.style,
                    ...customStyles.rows?.style,
                },
            },
            headRow: {
                ...baseStyles.headRow,
                ...customStyles.headRow,
                style: {
                    ...baseStyles.headRow?.style,
                    ...customStyles.headRow?.style,
                },
            },
            headCells: {
                ...baseStyles.headCells,
                ...customStyles.headCells,
                style: {
                    ...baseStyles.headCells?.style,
                    ...customStyles.headCells?.style,
                },
            },
        }
    }, [customStyles])

    return (
    <div>
            <Card className={`${flush ? 'mb-0 shadow-none' : 'mb-3 shadow-sm'} border-0`} style={{ borderRadius: 0 }}>
                {!showHeader ? null : <Card.Header className={`bg-white border-0 ${flush ? 'p-0' : 'pb-0 pt-3 px-3'}`}>
                    <div className="d-flex flex-column flex-lg-row align-items-lg-start justify-content-between gap-3">
                        <div>
                            {!showTitle ? null : (
                                <div className="mb-2">
                                    <h6 className="mb-2 fw-bold text-uppercase" style={{ color: dashboardTableBlue, fontSize: '0.95rem', letterSpacing: '0.02em' }}>
                                        {title}
                                    </h6>
                                    <div style={{ height: 1, backgroundColor: 'rgba(91, 155, 213, 0.35)' }} />
                                </div>
                            )}
                            {!showSearch ? null : <div style={{ maxWidth: '320px' }}>
                                <Form.Group controlId={`${title}-search`}>
                                    <Form.Control
                                        type="text"
                                        placeholder="Search..."
                                        value={filterText}
                                        onChange={e => setFilterText(e.target.value)}
                                        size="sm"
                                    />
                                </Form.Group>
                            </div>}
                        </div>

                        <div className={headerActionsFullWidth ? 'w-100' : 'd-flex flex-wrap gap-2 justify-content-lg-end ms-lg-auto'}>
                            {!showExportActions ? null : <TableExportActions
                                data={exportRows}
                                filename={exportFilename}
                                className="d-none d-md-flex flex-wrap gap-2 justify-content-lg-end"
                                disabled={exportRows.length === 0}
                            />}
                            {headerActions}
                        </div>
                    </div>
                </Card.Header>}
                <Card.Body className={flush ? 'p-0' : undefined}>
                    <div>
                        <DataTableStyleSheetManager>
                            <DataTable
                                columns={columns}
                                data={filteredData}
                                defaultSortFieldId={id}
                                defaultSortAsc={false}
                                dense
                                responsive
                                fixedHeader
                                pagination
                                paginationPerPage={paginationPerPage}
                                paginationRowsPerPageOptions={paginationRowsPerPageOptions}
                                fixedHeaderScrollHeight={height || '300px'}
                                onRowClicked={onRowClicked}
                                pointerOnHover={pointerOnHover}
                                highlightOnHover={pointerOnHover}

                                customStyles={dataTableStyles}
                            />
                        </DataTableStyleSheetManager>
                    </div>
                </Card.Body>
            </Card>
        </div>
    )
}
